import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HTMLReactParser from 'html-react-parser';
import { handlePrint, safeNavigate } from '../../util';
import { logger } from '../../util/logger';

interface DefinitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  juriID: string;
  renID: string;
  refID: string;
  pageID?: string;
}

const DefinitionModal: React.FC<DefinitionModalProps> = ({
  isOpen,
  onClose,
  juriID,
  renID,
  refID,
  pageID = 'demo',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [definitionData, setDefinitionData] = useState<string>('');
  const [error, setError] = useState<string>('');

  const loadDefinitionData = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      // For demo purposes, we'll simulate the API call
      // In real implementation, this would be: pagecfwindowdispDefinitiondata.cfm?juriID=${juriID}&renID=${renID}&refID=${refID}&pageID=${pageID}

      // Simulate API delay
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });

      // Mock definition data
      const mockData = `
        <div style="padding: 20px;">
          <h3>Definition Details</h3>
          <div style="margin: 15px 0;">
            <strong>Jurisdiction ID:</strong> ${juriID}
          </div>
          <div style="margin: 15px 0;">
            <strong>Rendition ID:</strong> ${renID}
          </div>
          <div style="margin: 15px 0;">
            <strong>Reference ID:</strong> ${refID}
          </div>
          <div style="margin: 15px 0;">
            <strong>Page ID:</strong> ${pageID}
          </div>
          <hr style="margin: 20px 0;" />
          <div style="background: #f8f9fa; padding: 15px; border-radius: 5px;">
            <h4>Definition Content</h4>
            <p>This is a demo definition content. In the real implementation, this would be fetched from the server using the provided parameters.</p>
            <p>The definition would contain the actual legal definition text, references, and related information.</p>
          </div>
        </div>
      `;

      setDefinitionData(mockData);
    } catch (error_) {
      setError('Failed to load definition data');
      if (process.env.NODE_ENV === 'development') {
        logger.error('Error loading definition:', error_);
      }
    } finally {
      setIsLoading(false);
    }
  }, [juriID, renID, refID, pageID]);

  useEffect(() => {
    if (isOpen) {
      safeNavigate(loadDefinitionData);
    }
  }, [isOpen, loadDefinitionData]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (event.target === event.currentTarget) {
        onClose();
      }
    }
  };

  const handleCopyClick = async (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.preventDefault();
    try {
      const temporaryDiv = document.createElement('div');
      temporaryDiv.innerHTML = definitionData;
      const textContent =
        temporaryDiv.textContent ?? temporaryDiv.textContent ?? '';
      await navigator.clipboard.writeText(textContent);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Failed to copy to clipboard:', error);
      }
    }
  };

  const handleCopyKeyDown = (event: React.KeyboardEvent) => {
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar'
    ) {
      event.preventDefault();
      void handleCopyClick(event);
    }
  };

  const handlePrintClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    handlePrint(definitionData);
  };

  const handlePrintKeyDown = (event: React.KeyboardEvent) => {
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar'
    ) {
      event.preventDefault();
      handlePrintClick(event);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      role="button"
      tabIndex={0}
      data-v2-shell-modal-overlay=""
      style={{
        display: 'block',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1050,
      }}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      aria-label="Close modal backdrop"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <div
          className="modal-content v3-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modalTitle"
        >
          <div className="modal-header">
            <h4 className="modal-title" id="modalTitle">
              Definitions
            </h4>
            <div className="text-end mt-1 ms-auto">
              <span
                className="pointer"
                data-toggle="tooltip"
                data-placement="bottom"
                data-bs-original-title="Copy to clipboard"
              >
                {' '}
                <Link
                  className="ao_copyPrint"
                  to="#"
                  onClick={(event) => {
                    void handleCopyClick(event);
                  }}
                  onKeyDown={handleCopyKeyDown}
                  aria-label="Copy to clipboard"
                  data-toggle="tooltip"
                  data-placement="bottom"
                  data-bs-original-title="Copy to clipboard"
                >
                  <img
                    src="/assets/images/newdesign/copy.svg"
                    width="15"
                    alt="copy"
                    style={{ paddingLeft: '3px', paddingRight: '3px' }}
                  />
                  <span id="copyToClipboarddef">Copy</span>
                </Link>
              </span>
              <Link
                to="#"
                className="ao_copyPrint pointer"
                onClick={handlePrintClick}
                onKeyDown={handlePrintKeyDown}
                aria-label="Copy to clipboard"
                data-toggle="tooltip"
                data-placement="bottom"
                data-bs-original-title="Copy to clipboard"
              >
                <img
                  src="/assets/images/newdesign/print.svg"
                  width="17"
                  alt="print"
                  style={{ paddingLeft: '3px', paddingRight: '3px' }}
                />
                <span id="printToClipboarddef">Print</span>
              </Link>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white ms-3"
              onClick={onClose}
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div className="modal-body" id="defPrintSectionEP">
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                  style={{ marginRight: '10px' }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                Loading Definition...
              </div>
            ) : error ? (
              <div
                style={{ color: 'red', textAlign: 'center', padding: '20px' }}
              >
                {error}
              </div>
            ) : (
              <span id="defData">{HTMLReactParser(definitionData)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefinitionModal;
